import { useEffect, useRef, useState, type ReactNode } from 'react'
import { appStore, useAppState } from '../app/state'
import { loadProject, useConnection } from '../app/data'

export type Values = Record<string, unknown>
export interface Field {
  name: string
  label: string
  type?: 'text' | 'password' | 'number' | 'date' | 'datetime-local' | 'textarea' | 'select' | 'checkbox'
  options?: readonly { value: string; label: string }[]
  required?: boolean
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  wide?: boolean
}
export function Fields({
  fields,
  values,
  onChange,
}: {
  fields: Field[]
  values: Values
  onChange: (key: string, value: string | number | boolean) => void
}) {
  return (
    <div className="form-grid">
      {fields.map((f) => (
        <label key={f.name} className={`form-field ${f.wide ? 'wide' : ''}`}>
          <span>{f.label}</span>
          {f.type === 'select' ? (
            <select
              disabled={f.disabled}
              required={f.required !== false}
              value={String(values[f.name] ?? '')}
              onChange={(e) => onChange(f.name, e.target.value)}
            >
              <option value="">Выберите…</option>
              {f.options?.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : f.type === 'textarea' ? (
            <textarea
              required={f.required !== false}
              value={String(values[f.name] ?? '')}
              onChange={(e) => onChange(f.name, e.target.value)}
            />
          ) : f.type === 'checkbox' ? (
            <input
              type="checkbox"
              checked={Boolean(values[f.name])}
              onChange={(e) => onChange(f.name, e.target.checked)}
              required={f.required !== false}
            />
          ) : (
            <input
              disabled={f.disabled}
              type={f.type ?? 'text'}
              required={f.required !== false}
              min={f.min}
              max={f.max}
              step={f.type === 'number' ? (f.step ?? 'any') : undefined}
              value={String(values[f.name] ?? '')}
              onInput={(e) =>
                onChange(
                  f.name,
                  f.type === 'number' && e.currentTarget.value !== ''
                    ? Number(e.currentTarget.value)
                    : e.currentTarget.value,
                )
              }
              onChange={(e) =>
                onChange(
                  f.name,
                  f.type === 'number' && e.target.value !== '' ? Number(e.target.value) : e.target.value,
                )
              }
            />
          )}
        </label>
      ))}
    </div>
  )
}

export function ActionForm({
  fields,
  initial,
  submit,
  onDone,
  label = 'Сохранить',
  children,
  draftKey,
}: {
  fields: Field[] | ((values: Values) => Field[])
  initial: Values
  submit: (values: Values, commandId: string) => Promise<void | { queued: true }>
  onDone?: () => void
  label?: string
  children?: ReactNode | ((values: Values, setValue: (key: string, value: unknown) => void) => ReactNode)
  draftKey?: string
}) {
  const session = useConnection().session
  const storageKey = draftKey && session?.user.id
    ? 'magistral.draft.' + encodeURIComponent(session.user.id) + '.' + draftKey
    : ''
  const [values, setValues] = useState(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey)
        if (saved) return { ...initial, ...JSON.parse(saved) }
      } catch {
        /* A draft must not prevent opening the form. */
      }
    }
    return initial
  })
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(values))
      } catch {
        /* Storage may be disabled by the browser. */
      }
    }
  }, [storageKey, values])
  const [busy, setBusy] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshStatus, setRefreshStatus] = useState('')
  const [refreshError, setRefreshError] = useState('')
  const [error, setError] = useState('')
  const { role } = useAppState()
  const retry = useRef({ json: '', key: '' })
  const locked = useRef(false)
  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault()
        if (locked.current) return
        locked.current = true
        setBusy(true)
        setError('')
        setRefreshStatus('')
        setRefreshError('')
        const json = JSON.stringify(values)
        if (retry.current.json !== json) retry.current = { json, key: crypto.randomUUID() }
        try {
          const outcome = await submit(values, retry.current.key)
          if (storageKey) {
            try {
              localStorage.removeItem(storageKey)
            } catch {}
          }
          appStore.showToast(
            outcome?.queued ? 'Сохранено на устройстве' : 'Принято',
            outcome?.queued ? 'Ожидает отправки. Факт ещё не принят системой.' : 'Операция записана на сервере',
          )
          onDone?.()
        } catch (cause) {
          setError(cause instanceof Error ? cause.message : 'Не удалось сохранить')
        } finally {
          locked.current = false
          setBusy(false)
        }
      }}
    >
      <fieldset disabled={busy}>
        <Fields
          fields={typeof fields === 'function' ? fields(values) : fields}
          values={values}
          onChange={(key, value) => setValues({ ...values, [key]: value })}
        />
        {typeof children === 'function'
          ? children(values, (key, value) => setValues((current: Values) => ({ ...current, [key]: value })))
          : children}
      </fieldset>
      {error && (
        <div className="alert compact-button" role="alert">
          <div>
            <strong>{error}</strong>
            {refreshError && <p>{refreshError}</p>}
            <button
              type="button"
              className="button secondary"
              disabled={refreshing || busy}
              onClick={async () => {
                if (locked.current) return
                locked.current = true
                setRefreshing(true)
                setRefreshStatus('')
                setRefreshError('')
                try {
                  const result = await loadProject(role)
                  if (!result.ok) {
                    setRefreshError('Не удалось обновить данные: ' + result.error)
                    return
                  }
                  setError('')
                  setRefreshStatus(
                    'Данные обновлены. Введённые значения сохранены. Проверьте параметры формы и повторите сохранение.',
                  )
                } catch (cause) {
                  setRefreshError(
                    cause instanceof Error ? cause.message : 'Не удалось обновить данные. Повторите попытку.',
                  )
                } finally {
                  locked.current = false
                  setRefreshing(false)
                }
              }}
            >
              {refreshing ? 'Обновление…' : 'Обновить данные'}
            </button>
          </div>
        </div>
      )}
      {refreshStatus && <p role="status">{refreshStatus}</p>}
      {draftKey && (
        <p className="draft-note">
          Черновик сохраняется на этом устройстве для вашего аккаунта. Статус на сервере изменится только после подтверждения операции.
        </p>
      )}
      <div className="form-actions">
        <button className="button primary" disabled={busy || refreshing} type="submit">
          {busy ? 'Сохранение…' : label}
        </button>
      </div>
    </form>
  )
}
export const dateTime = (value: string) =>
  value
    ? new Date(value).toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow',
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '—'
export const localDateTime = (value: string) => {
  const date = new Date(value)
  return new Date(date.getTime() + 3 * 3600000).toISOString().slice(0, 16)
}
export const toISO = (value: unknown) => String(value) + ':00+03:00'
export const number = (value: number) =>
  (Object.is(value, -0) ? 0 : value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })
export function Empty({ children = 'Нет записей по выбранным условиям' }: { children?: ReactNode }) {
  return <div className="empty">{children}</div>
}
